import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Upload, X, AlertTriangle, FolderOpen } from 'lucide-react'
import { fileService } from '../services/fileService'

const PRIORITY_COLORS = {
  urgent: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  normal: 'bg-blue-100 text-blue-700',
  low: 'bg-gray-100 text-gray-600',
}

function UploadFilePage() {
  const navigate = useNavigate()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [filterMode, setFilterMode] = useState('needs_action') // needs_action | belum_upload | rejected | all
  const [uploadModal, setUploadModal] = useState(null) // { spk, fileType, schoolCode, existingFile }

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    setLoading(true)
    try {
      const res = await fileService.getRecap({ spkStatus: 'active' })
      if (res.success) {
        setData(res.data)
      }
    } catch (err) {
      console.error('Error fetching recap:', err)
    } finally {
      setLoading(false)
    }
  }

  const allSpks = data?.spks || []

  // Build flat list of actionable slots
  const actionableItems = []
  for (const spk of allSpks) {
    const allSlots = [...(spk.slots || []), ...(spk.customCoverSlots || [])]
    for (const slot of allSlots) {
      const isEmpty = !slot.file
      const isRejected = slot.file?.status === 'rejected'
      if (isEmpty || isRejected) {
        actionableItems.push({ spk, slot, isEmpty, isRejected })
      }
    }
  }

  // Filter
  const filteredItems = actionableItems.filter(item => {
    // Filter mode
    if (filterMode === 'belum_upload' && !item.isEmpty) return false
    if (filterMode === 'rejected' && !item.isRejected) return false
    // needs_action shows both empty and rejected (default)

    // Search
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      if (!item.spk.judul?.toLowerCase().includes(q) &&
          !item.spk.kode?.toLowerCase().includes(q) &&
          !item.spk.id?.toLowerCase().includes(q)) {
        return false
      }
    }
    return true
  })

  // Also compute "all SPK" view for the 'all' filter
  const allFiltered = filterMode === 'all'
    ? allSpks.filter(spk => {
        if (!searchQuery) return true
        const q = searchQuery.toLowerCase()
        return spk.judul?.toLowerCase().includes(q) ||
               spk.kode?.toLowerCase().includes(q) ||
               spk.id?.toLowerCase().includes(q)
      })
    : null

  // Counts
  const totalEmpty = actionableItems.filter(i => i.isEmpty).length
  const totalRejected = actionableItems.filter(i => i.isRejected).length

  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('id-ID', { day: 'numeric', month: 'short' })
  }

  const getSlotLabel = (slot) => {
    if (slot.type === 'isi') return 'ISI'
    if (slot.type === 'cover') return 'Cover'
    if (slot.type === 'cover_custom') return `Cover CS (${slot.label})`
    if (slot.type === 'sisipan') return `Sisipan (${slot.schoolCode})`
    return slot.label
  }

  const handleUploadClick = (spk, slot) => {
    setUploadModal({
      spk,
      fileType: slot.type,
      schoolCode: slot.schoolCode,
      existingFile: slot.file,
    })
  }

  const handleUploadSuccess = () => {
    setUploadModal(null)
    fetchData()
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <Upload className="w-7 h-7 text-blue-600" />
          Upload File
        </h1>
        <p className="text-gray-500 text-sm mt-1">Upload dan revisi file untuk SPK yang membutuhkan</p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-4">
        <button
          onClick={() => setFilterMode('needs_action')}
          className={`rounded-xl shadow-sm p-4 text-left transition-colors ${filterMode === 'needs_action' ? 'bg-blue-50 ring-2 ring-blue-400' : 'bg-white hover:bg-gray-50'}`}
        >
          <p className="text-sm text-gray-500">Total Perlu Aksi</p>
          <p className="text-2xl font-bold text-gray-800">{totalEmpty + totalRejected}</p>
        </button>
        <button
          onClick={() => setFilterMode('belum_upload')}
          className={`rounded-xl shadow-sm p-4 text-left transition-colors ${filterMode === 'belum_upload' ? 'bg-gray-200 ring-2 ring-gray-400' : 'bg-white hover:bg-gray-50'}`}
        >
          <p className="text-sm text-gray-500">Belum Upload</p>
          <p className="text-2xl font-bold text-gray-500">{totalEmpty}</p>
        </button>
        <button
          onClick={() => setFilterMode('rejected')}
          className={`rounded-xl shadow-sm p-4 text-left transition-colors ${filterMode === 'rejected' ? 'bg-red-50 ring-2 ring-red-400' : 'bg-white hover:bg-gray-50'}`}
        >
          <p className="text-sm text-gray-500">Perlu Revisi</p>
          <p className="text-2xl font-bold text-red-600">{totalRejected}</p>
        </button>
      </div>

      {/* Search & Filter */}
      <div className="flex gap-4 items-end">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Cari SPK..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg"
          />
        </div>
        <select
          value={filterMode}
          onChange={(e) => setFilterMode(e.target.value)}
          className="px-3 py-2 border rounded-lg text-sm"
        >
          <option value="needs_action">Perlu Aksi</option>
          <option value="belum_upload">Belum Upload saja</option>
          <option value="rejected">Perlu Revisi saja</option>
          <option value="all">Semua SPK</option>
        </select>
      </div>

      {/* Content */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-500">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
            Memuat data...
          </div>
        ) : filterMode === 'all' ? (
          /* All SPK view */
          allFiltered && allFiltered.length > 0 ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b text-gray-600">
                  <th className="px-4 py-3 text-left font-medium">SPK</th>
                  <th className="px-4 py-3 text-center font-medium w-24">Priority</th>
                  <th className="px-4 py-3 text-center font-medium w-24">Deadline</th>
                  <th className="px-4 py-3 text-center font-medium w-28">Kelengkapan</th>
                  <th className="px-4 py-3 text-center font-medium w-24">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {allFiltered.map(spk => (
                  <tr key={spk.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-800">{spk.judul}</p>
                      <p className="text-xs text-gray-400">{spk.kode}</p>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium uppercase ${PRIORITY_COLORS[spk.priority] || PRIORITY_COLORS.normal}`}>
                        {spk.priority}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-gray-600">{formatDate(spk.deadline)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-sm">{spk.kelengkapan.validated}/{spk.kelengkapan.total}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => navigate(`/spk/${spk.id}/files`)}
                        className="flex items-center gap-1 px-3 py-1.5 mx-auto text-xs text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100"
                      >
                        <FolderOpen className="w-3.5 h-3.5" />
                        Kelola
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="p-12 text-center text-gray-500">Tidak ada SPK ditemukan</div>
          )
        ) : (
          /* Actionable items view */
          filteredItems.length > 0 ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b text-gray-600">
                  <th className="px-4 py-3 text-left font-medium">SPK</th>
                  <th className="px-4 py-3 text-center font-medium w-24">Priority</th>
                  <th className="px-4 py-3 text-center font-medium w-24">Deadline</th>
                  <th className="px-4 py-3 text-left font-medium w-40">Slot File</th>
                  <th className="px-4 py-3 text-center font-medium w-28">Status</th>
                  <th className="px-4 py-3 text-center font-medium w-28">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredItems.map((item, idx) => (
                  <tr key={`${item.spk.id}-${item.slot.type}-${item.slot.schoolCode || ''}`} className={`hover:bg-gray-50 ${item.isRejected ? 'bg-red-50/30' : ''}`}>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => navigate(`/spk/${item.spk.id}/files`)}
                        className="text-left hover:text-blue-600"
                      >
                        <p className="font-medium text-gray-800">{item.spk.judul}</p>
                        <p className="text-xs text-gray-400">{item.spk.kode}</p>
                      </button>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium uppercase ${PRIORITY_COLORS[item.spk.priority] || PRIORITY_COLORS.normal}`}>
                        {item.spk.priority}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-gray-600">{formatDate(item.spk.deadline)}</td>
                    <td className="px-4 py-3">
                      <span className="font-medium text-gray-700">{getSlotLabel(item.slot)}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {item.isEmpty ? (
                        <span className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-500">Belum upload</span>
                      ) : (
                        <span className="px-2 py-0.5 rounded text-xs bg-red-100 text-red-700" title={item.slot.file?.reject_reason}>
                          Ditolak v{item.slot.file?.version}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => handleUploadClick(item.spk, item.slot)}
                        className="flex items-center gap-1 px-3 py-1.5 mx-auto text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                      >
                        <Upload className="w-3.5 h-3.5" />
                        {item.isRejected ? 'Revisi' : 'Upload'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="p-12 text-center text-gray-500">
              <Upload className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="font-medium">Semua file sudah terupload</p>
              <p className="text-sm mt-1">Tidak ada slot file yang memerlukan aksi</p>
            </div>
          )
        )}
      </div>

      {/* Upload Modal */}
      {uploadModal && (
        <UploadModal
          spkId={uploadModal.spk.id}
          spkJudul={uploadModal.spk.judul}
          fileType={uploadModal.fileType}
          schoolCode={uploadModal.schoolCode}
          existingFile={uploadModal.existingFile}
          onClose={() => setUploadModal(null)}
          onSuccess={handleUploadSuccess}
        />
      )}
    </div>
  )
}

// Upload Modal
function UploadModal({ spkId, spkJudul, fileType, schoolCode, existingFile, onClose, onSuccess }) {
  const fileInputRef = useRef(null)
  const [selectedFile, setSelectedFile] = useState(null)
  const [pageCount, setPageCount] = useState('')
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState('')

  const isRevision = existingFile?.status === 'rejected'
  const needsPageCount = fileType === 'isi' || fileType === 'sisipan'

  const getLabel = () => {
    if (fileType === 'isi') return 'ISI'
    if (fileType === 'cover') return 'COVER'
    if (fileType === 'cover_custom') return `COVER CUSTOM (${schoolCode})`
    if (fileType === 'sisipan') return `SISIPAN (${schoolCode})`
    return fileType
  }

  const handleUpload = async () => {
    if (!selectedFile) { setError('Pilih file terlebih dahulu'); return }
    if (needsPageCount && !pageCount) { setError('Jumlah halaman harus diisi'); return }

    setUploading(true)
    setError('')
    try {
      const formData = new FormData()
      formData.append('file', selectedFile)
      formData.append('spkId', spkId)
      formData.append('fileType', fileType)
      if (schoolCode) formData.append('schoolCode', schoolCode)
      if (needsPageCount) formData.append('pageCount', pageCount)

      const res = await fileService.upload(formData)
      if (res.success) onSuccess()
      else setError(res.message || 'Gagal upload')
    } catch (err) {
      setError(err.response?.data?.message || 'Gagal upload file')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-md">
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <Upload className="w-5 h-5 text-blue-600" />
            {isRevision ? 'Upload Revisi' : 'Upload File'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="font-medium text-gray-800">{spkJudul}</p>
            <p className="text-sm text-gray-500 mt-0.5">Slot: <span className="font-medium">{getLabel()}</span></p>
          </div>

          {isRevision && existingFile?.reject_reason && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm font-medium text-red-700 flex items-center gap-1.5">
                <AlertTriangle className="w-4 h-4" />
                Ditolak (v{existingFile.version})
              </p>
              <p className="text-sm text-red-600 mt-1">Alasan: {existingFile.reject_reason}</p>
            </div>
          )}

          {error && <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>}

          <div
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-blue-300 rounded-xl p-8 text-center cursor-pointer hover:bg-blue-50 transition-colors"
          >
            <Upload className="w-10 h-10 text-blue-400 mx-auto mb-2" />
            {selectedFile ? (
              <p className="text-blue-600 font-medium">{selectedFile.name}</p>
            ) : (
              <>
                <p className="text-blue-600 font-medium">Klik untuk pilih file</p>
                <p className="text-gray-500 text-sm">PDF, JPG, PNG, TIFF</p>
              </>
            )}
            <input
              ref={fileInputRef}
              type="file"
              onChange={(e) => e.target.files[0] && setSelectedFile(e.target.files[0])}
              accept=".pdf,.jpg,.jpeg,.png,.tiff,.tif"
              className="hidden"
            />
          </div>

          {needsPageCount && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Jumlah Halaman *</label>
              <input
                type="number"
                value={pageCount}
                onChange={(e) => setPageCount(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Contoh: 256"
              />
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              onClick={handleUpload}
              disabled={uploading}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <Upload className="w-4 h-4" />
              {uploading ? 'Uploading...' : isRevision ? 'Upload Revisi' : 'Upload'}
            </button>
            <button onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
              Batal
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default UploadFilePage
